
import PdfViewer from '@/components/pdf-viewer.vue'
import DocumentIndicator from '@/components/request-signature/document-indicator.vue'
import { useAppStore } from '@/store/app'
import { Document } from '@/types/document'
import { getInitials } from '@/utils/name'
import Vue from 'vue'
import SignerLookup from '../components/signer-lookup.vue'

type QuickView = 'completed' | 'waiting' | 'revoked'

export default Vue.extend({
  name: 'Documents',
  components: {
    DocumentIndicator,
    PdfViewer,
    SignerLookup
  },
  setup () {
    const app = useAppStore()

    return {
      app
    }
  },
  data () {
    return {
      loading: false,
      loadingMore: false,
      page: 1,
      pageSize: 50,
      pageCount: 0,
      totalItems: 0,
      lastCreatedAt: 0,
      options: {},
      signerId: '',
      headers: [
        { text: 'Template', value: 'title', sortable: false },
        { text: 'Requester', value: 'requester', sortable: false },
        { text: 'Last change', value: 'timestamp', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      documents: new Array<Document>(),
      activeView: '' as QuickView,
      showInspectDialog: false,
      showConfirmDialog: false,
      toView: {} as { name: string; url: string },
      toDelete: {} as { title: string; reference: string; status: string; signer: { name: string; lastname: string; email: string } },
      childInterface: {
        clear: () => Object,
        refetch: async () => Object
      }
    }
  },
  async mounted () {
    this.signerId = this.$route.params.signer
    this.fetch(this.pageSize)
    this.getNextDocuments()
  },
  methods: {
    // Setting the interface when emitted from child
    getChildInterface (childInterface: any) {
      this.childInterface = childInterface
    },
    signerChanged (signer: { name: string; id: string; lastname: string }) {
      this.signerId = signer ? signer.id : ''
      this.search()
    },
    getInitials (name: string) {
      return getInitials(name)
    },
    async fetch (pageSize = 20, lastCreatedAt?: number) {
      this.loading = true
      const client = this.app.selectedClient?.id ?? ''
      const params = []

      if (lastCreatedAt) {
        params.push(`lastCreatedAt=${lastCreatedAt}`)
      }

      if (this.signerId) {
        params.push(`signerId=${this.signerId}`)
      }

      const res = await fetch(`${process.env.VUE_APP_API_URL}/${client}/documents?pageSize=${pageSize}&${params.join('&')}`)
      const response = await res.json()

      this.documents = [...this.documents, ...response.documents
        .map((document: {signed?: number; revoked?: number; createdAt: number;}) => ({
          ...document,
          status: document.signed
            ? 'signed'
            : document.revoked
              ? 'revoked'
              : 'waiting',
          timestamp: (document.signed || undefined) ?? (document.revoked || undefined) ?? document.createdAt ?? Date.now()
        }))]
      this.loadingMore = false
      this.totalItems = response.totalDocs
      this.lastCreatedAt = response.lastCreatedAt
      this.pageSize = response.pageSize
      this.loading = false
    },
    actionTime (timestamp: string) {
      if (!timestamp) {
        return undefined
      }

      const actionTimestamp = Number(timestamp)
      const utcDate = new Date(actionTimestamp)

      const time = utcDate.toLocaleTimeString().split(':')

      return `${utcDate.toLocaleDateString()} ${time[0]}:${time[1]}`
    },
    closeDialog () {
      this.showInspectDialog = false
      this.showConfirmDialog = false
      this.toView = {} as { name: string; url: string }
      this.toDelete = {} as { title: string; reference: string; status: string; signer: { name: string; lastname: string; email: string } }
    },
    async viewDocument (document: { name: string; reference: string }) {
      const client = this.app.selectedClient?.id ?? ''
      const res = await fetch(`${process.env.VUE_APP_API_URL}/${client}/documents/${document.reference}`)
      const url = await res.text()
      this.toView = { name: document.name, url }
      this.showInspectDialog = true
    },
    deleteDocument (document: { title: string; reference: string; status: string; signer: { name: string; lastname: string; email: string } }) {
      this.toDelete = document
      this.showConfirmDialog = true
    },
    async confirmDeleteDocument () {
      const client = this.app.selectedClient?.id ?? ''
      await fetch(`${process.env.VUE_APP_API_URL}/${client}/actions/${this.toDelete.reference}`, {
        method: 'DELETE'
      })
      this.closeDialog()
      this.fetch(this.pageSize)
    },
    async fetchMoreDocuments () {
      if (this.documents.length < this.totalItems) {
        this.loadingMore = true
        this.fetch(this.pageSize, this.lastCreatedAt)
      }
    },
    search () {
      this.documents = []
      this.lastCreatedAt = 0
      this.fetch(this.pageSize, this.lastCreatedAt)
    },
    clear () {
      this.signerId = ''
      this.search()
    },
    getNextDocuments () {
      window.onscroll = () => {
        // ts-expect-error: blah
        // const bottomOfWindow = (this.$refs.infiniteList as HTMLElement).getBoundingClientRect().bottom < window.innerHeight
        const bottomOfWindow = document.documentElement.scrollTop + window.innerHeight <= document.documentElement.offsetHeight
        if (bottomOfWindow && !this.loading) {
          this.fetchMoreDocuments()
        }
      }
    }
  }
})
